import {
	AssetPropertiesProps,
} from './components/AssetProperties';
import {
	AssetsTab,
	OtherTab,
	SensorsTab,
	SimulationsTab,
} from './components';
import React, { useMemo, useRef } from 'react';
import StyledPropertyPane, {
	Actions,
	StyledStylovyzeTabs,
} from './PropertyPane.styles';

import { AlertProperties } from './components/AlertProperties';
import { Close } from '@mui/icons-material';
import { FacilityProperties } from './components/FacilityProperties';
import { FeatureItem } from '../AssetGrid/utils/types';
import { IconButton } from '@mui/material';
import { IncidentProperties } from './components/IncidentProperties';
import { TabGroup } from '@innovyze/stylovyze';
import _ from 'lodash';
import { notFalsy } from '../AssetGrid/utils/helpers';
import { useGlobalization } from '@Translations/useGlobalization';
import { PropertyPaneProps } from './PropertyPaneProps';
import { SensorPropertiesProps } from './components/SensorProperties';

export enum TabTypes {
	alert = 'alert',
	asset = 'asset',
	sensor = 'sensor',
	incident = 'incident',
	spatial = 'spatial',
	facility = 'facility',
	simulation = 'simulation',
}

const defaultTabs = [
	TabTypes.alert,
	TabTypes.asset,
	TabTypes.sensor,
	TabTypes.incident,
	TabTypes.spatial,
	TabTypes.facility,
]; // Don't include simulation tab by default

export interface Props extends PropertyPaneProps {
	/** Tabs to show in the property pane */
	tabsToDisplay?: TabTypes[];
	/** show inspection properties */
	showInspections?: boolean;
	/** function for view details button in menu of asset properties, button is only shown if function passed through */
	viewAssetDetails?: AssetPropertiesProps['viewDetails'];
	/** asset types to show view details button for */
	viewDetailsForAssetTypes?: AssetPropertiesProps['viewDetailsForAssetTypes'];
	/** function for view details on inspection, button is only shown if function passed through */
	viewInspection?: AssetPropertiesProps['viewInspection'];
	/** function for view details on inspection, button is only shown if function passed through */
	viewTask?: AssetPropertiesProps['viewTask'];
	/** function for view details on inspection, button is only shown if function passed through */
	viewEvent?: AssetPropertiesProps['viewEvent'];
	/** function to call from workspaces to add chart to current workspace */
	addChartToCurrentWorkspace?: SensorPropertiesProps['addChartToCurrentWorkspace'];
	/** current workspace id */
	currentWorkspaceId?: SensorPropertiesProps['currentWorkspaceId'];
}

const filterForAlertId = (
	alerts: FeatureItem[],
	resolvedAlert: string,
): string[] => {
	return alerts
		.filter(alert => {
			const [alertId] = alert.id.split('-');
			return alertId === resolvedAlert;
		})
		.map(alert => alert.id);
};

const PropertyPane = ({
	items,
	deselectAssets,
	traceFromAsset,
	highlightAssets,
	zoomToAssets,
	tabsToDisplay = defaultTabs,
	showInspections,
	viewAssetDetails,
	viewDetailsForAssetTypes,
	viewInspection,
	viewTask,
	viewEvent,
	addChartToCurrentWorkspace,
	currentWorkspaceId,
	simulations,
	simulationsLoading,
}: Props): JSX.Element => {
	const { t } = useGlobalization();
	const ref = useRef<HTMLDivElement | null>(null);
	const {
		alerts,
		sensors,
		assets,
		incidents,
		spatial,
		facilities,
	} = useMemo(() => {
		const { alert, asset, sensor, incident, spatial, unknown, facility } = {
			alert: [],
			asset: [],
			sensor: [],
			incident: [],
			spatial: [],
			unknown: [],
			facility: [],
			..._.groupBy(items, 'type'),
		};
		return {
			alerts: alert,
			assets: asset,
			sensors: sensor,
			incidents: incident,
			facilities: facility,
			spatial: [...spatial, ...unknown],
		};
	}, [items]);

	const onAlertResolved = (alertId: string) => {
		const assetsToRemove = filterForAlertId(alerts, alertId);
		deselectAssets?.(assetsToRemove);
	};

	const displayTab = (tabType: TabTypes): boolean => {
		return tabsToDisplay.includes(tabType);
	};

	const deselectAll = () => {
		deselectAssets?.(items?.map((item: FeatureItem) => item.id) ?? []);
	};

	const alertsTab = (
		<>
			{alerts.map(
				(asset: FeatureItem, i: React.Key | null | undefined) => (
					<AlertProperties
						key={i}
						item={asset}
						onAlertResolved={onAlertResolved}
					/>
				),
			)}
		</>
	);

	const incidentsTab = (
		<>
			{incidents.map((incident, i) => (
				<IncidentProperties key={i} item={incident} />
			))}
		</>
	);

	const facilitiesTab = (
		<>
			{facilities.map((facility, i) => (
				<FacilityProperties key={i} item={facility} />
			))}
		</>
	);

	const [activeTab, setActiveTab] = React.useState<undefined | number>();
	const tabs: TabGroup[] = useMemo(
		() =>
			[
				displayTab(TabTypes.alert) && {
					title: `${t('Alerts', {
						context: 'Property pane tab heading',
					})} [${alerts.length}]`,
					content: alertsTab,
					disabled: !alerts.length,
				},
				displayTab(TabTypes.incident) && {
					title: `${t('Incidents', {
						context: 'Property pane tab heading',
					})} [${incidents.length}]`,
					content: incidentsTab,
					disabled: !incidents.length,
				},
				displayTab(TabTypes.facility) && {
					title: `${t('Facilities', {
						context: 'Property pane tab heading',
					})} [${facilities.length}]`,
					content: facilitiesTab,
					disabled: !facilities.length,
				},
				displayTab(TabTypes.sensor) && {
					title: `${t('Sensors', {
						context: 'Property pane tab heading',
					})} [${sensors.length}]`,
					content: (
						<SensorsTab
							items={sensors}
							highlightAssets={highlightAssets}
							addChartToCurrentWorkspace={
								addChartToCurrentWorkspace
							}
							currentWorkspaceId={currentWorkspaceId}
						/>
					),
					disabled: !sensors.length,
				},
				displayTab(TabTypes.asset) && {
					title: `${t('Assets', {
						context: 'Property pane tab heading',
					})} [${assets.length}]`,
					content: (
						<AssetsTab
							items={assets}
							highlightAssets={highlightAssets}
							traceFromAsset={traceFromAsset}
							showInspections={showInspections}
							viewDetails={viewAssetDetails}
							viewDetailsForAssetTypes={viewDetailsForAssetTypes}
							viewInspection={viewInspection}
							viewTask={viewTask}
							viewEvent={viewEvent}
						/>
					),
					disabled: !assets.length,
				},
				displayTab(TabTypes.spatial) && {
					title: `${t('Spatial', {
						context: 'Property pane tab heading',
					})} [${spatial.length}]`,
					content: (
						<OtherTab
							items={spatial}
							highlightAssets={highlightAssets}
						/>
					),
					disabled: !spatial.length,
				},
				displayTab(TabTypes.simulation) && {
					title: simulationsLoading
						? `${t('Simulations', {
								context: 'Property pane tab heading',
						  })}`
						: `${t('Simulations', {
								context: 'Property pane tab heading',
						  })} [${simulations?.length ?? 0}]`,
					content: (
						<SimulationsTab
							items={simulations}
							setActiveTab={setActiveTab}
							simulationsTabIndex={tabsToDisplay.indexOf(
								TabTypes.simulation,
							)}
						/>
					),
					loading: simulationsLoading,
					disabled: !simulations?.length,
				},
			].filter(notFalsy),
		[
			alerts,
			incidents,
			sensors,
			assets,
			spatial,
			simulations,
			simulationsLoading,
		],
	);

	return (
		<StyledPropertyPane
			data-testing-id="property-pane"
			open={!!items && items.length > 0}>
			<Actions>
				<IconButton onClick={deselectAll}>
					<Close />
				</IconButton>
			</Actions>

			{items && items.length > 0 && (
				<StyledStylovyzeTabs
					tabs={tabs}
					baseId="property-pane"
					dataCy="tabs"
					TabsProps={{ ref }}
					activeTab={activeTab}
				/>
			)}
		</StyledPropertyPane>
	);
};

export default React.memo(PropertyPane);
